import React, { useCallback, useState } from 'react';
import {
  AppCheckbox,
  AppDatePicker,
  AppInputNumber,
  AppModal,
  AppNoInfo,
  AppRemixIcon,
  AppSingleSelect,
  AppTextArea,
} from '@/layout/AppComponents';
import axios from 'axios';
import { Formik } from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import ReactDOMServer from 'react-dom/server';
import { axiosPath } from '../../../../../routing/helpers/router';
import colors from '../../../../../constants/colors';
import errorHandler from '@/layout/components/error/ErrorHandler';
import Translator from '../../../../components/helpers/Translator';
import * as currentPageAction from '../../../../../store/actions/currentPageAction';

export default function ObligationNextDateSettings({
  resourceObligation,
  size,
  loadObligationTab,
  elementID,
}) {
  const { moduleBridgeHex } = useParams();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const [periodOptions] = useState([
    { label: ReactDOMServer.renderToString(
      <Translator
        file="module-resourceManager"
        name="resource.days"
      />,
    ), value: 'days' },
    { label: ReactDOMServer.renderToString(
      <Translator
        file="module-resourceManager"
        name="resource.weeks"
      />,
    ), value: 'weeks' },
    { label: ReactDOMServer.renderToString(
      <Translator
        file="module-resourceManager"
        name="resource.months"
      />,
    ), value: 'months' },
    { label: ReactDOMServer.renderToString(
      <Translator
        file="module-resourceManager"
        name="resource.years"
      />,
    ), value: 'years' },
  ]);
  const [validationSchema] = useState(
    Yup.object().shape({
      periodType: Yup.string().when('noNextDate', {
        is: (v) => v === false,
        then: (schema) =>
          schema.required(
            ReactDOMServer.renderToString(
              <Translator
                file="module-resourceManager"
                name="resource.required"
              />,
            ),
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      period: Yup.string().when('noNextDate', {
        is: (v) => v === false,
        then: (schema) =>
          schema.required(
            ReactDOMServer.renderToString(
              <Translator
                file="module-resourceManager"
                name="resource.required"
              />,
            ),
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
      firstPeriodStartDate: Yup.string().when('noNextDate', {
        is: (v) => v === false,
        then: (schema) =>
          schema.required(
            ReactDOMServer.renderToString(
              <Translator
                file="module-resourceManager"
                name="resource.required"
              />,
            ),
          ),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  );

  // > UPDATE NEXT DATE SETTINGS
  const updateNextDateSettings = useCallback(
    async (payload) => {
      axios
        .post(
          axiosPath(
            'api_r_user_module_resourceManager_resourceObligation_nextDate_set',
          ),
          {
            moduleBridgeHex,
            resourceObligationID: resourceObligation?.id,
            payload,
          },
        )
        .then(() => {
          dispatch(currentPageAction.change('updateCard', elementID, true));
          setShowModal(false);
          loadObligationTab();
        })
        .catch((e) => dispatch(errorHandler(e)));
    },
    [moduleBridgeHex, resourceObligation, loadObligationTab],
  );
  // < UPDATE NEXT DATE SETTINGS

  return (
    <>
      {size === 'sm' ? (
        <AppRemixIcon
          icon="settings-5"
          color={
            resourceObligation?.nextDateSettings
              ? colors.grey400
              : colors.state2
          }
          filled={!resourceObligation?.nextDateSettings}
          onClick={() => setShowModal(true)}
          className="pointer"
          title={ReactDOMServer.renderToString(
            <Translator
              file="module-resourceManager"
              name="resource.set.frequency"
            />,
          )}
        />
      ) : size === 'md' ? (
        <span
          className="btn btn-xs btn-warning"
          onClick={() => setShowModal(true)}
        >
          <AppRemixIcon icon="settings-5" className="mr-1" />
          <Translator file="module-resourceManager" name="resource.configure" />
        </span>
      ) : (
        <AppNoInfo
          text={
            <span
              className="btn btn-warning"
              onClick={() => setShowModal(true)}
            >
              <Translator
                file="module-resourceManager"
                name="resource.set.frequency"
              />
            </span>
          }
          icon={
            <AppRemixIcon size={40} icon="settings-5" color={colors.state2} />
          }
        />
      )}
      <AppModal
        title={
          <Translator
            file="module-resourceManager"
            name="resource.set.frequency"
          />
        }
        show={showModal}
        onClose={() => setShowModal(false)}
        size="lg"
      >
        <Formik
          initialValues={{
            periodType:
              resourceObligation?.nextDateSettings?.periodType ||
              resourceObligation?.obligation?.nextDateDefaultSettings
                ?.periodType ||
              'months',
            period:
              resourceObligation?.nextDateSettings?.period ||
              resourceObligation?.obligation?.nextDateDefaultSettings?.period ||
              1,
            firstPeriodStartDate:
              resourceObligation?.nextDateSettings?.firstPeriodStartDate || '',
            noNextDate: false,
          }}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={validationSchema}
          onSubmit={updateNextDateSettings}
        >
          {({ values, setFieldValue, errors, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-6">
                  <AppSingleSelect
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.set.frequency"
                      />,
                    )}
                    icon={<AppRemixIcon icon="calendar-event" />}
                    options={periodOptions}
                    value={values.periodType}
                    onChange={(v) => setFieldValue('periodType', v)}
                    error={errors.periodType}
                    isDisabled={values.noNextDate}
                  />

                  <AppInputNumber
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.number.of.options.type"
                        variables={{
                          periodType: periodOptions.find(
                            (p) => p.value === values.periodType,
                          )?.label,
                        }}
                      />,
                    )}
                    value={values.period}
                    icon={<AppRemixIcon icon="calendar-2" />}
                    onChange={(v) => setFieldValue('period', v)}
                    error={errors.period}
                    disabled={values.noNextDate}
                  />

                  <AppDatePicker
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.start.date"
                      />,
                    )}
                    value={values.firstPeriodStartDate}
                    onChange={(v) => setFieldValue('firstPeriodStartDate', v)}
                    error={errors.firstPeriodStartDate}
                    popperClassName="react-datepicker-popper react-datepicker-popper-1000"
                    showYearDropdown
                    showMonthDropdown
                    hasToggleType
                    isDisabled={
                      resourceObligation?.nextDateSettings
                        ?.firstPeriodStartDate || values.noNextDate
                    }
                  />

                  <AppTextArea
                    title={ReactDOMServer.renderToString(
                      <Translator
                        file="module-resourceManager"
                        name="resource.frequency.indicated.obligation"
                      />,
                    )}
                    value={
                      resourceObligation?.obligation?.obligationLegalNextDate
                    }
                    isReadOnly
                  />
                  <p
                    style={{
                      fontSize: '10px',
                      color: colors.grey500,
                    }}
                  >
                    <Translator
                      file="module-resourceManager"
                      name="resource.frequency.shown.information.only.configure.fields"
                    />
                  </p>
                </div>
                <div className="col-6">
                  <AppCheckbox
                    value={values.noNextDate}
                    onChange={(v) => setFieldValue('noNextDate', v)}
                    hasBorder
                  >
                    <Translator
                      file="module-resourceManager"
                      name="resource.no.regularity"
                    />
                  </AppCheckbox>
                  {values.noNextDate && (
                    <div className="alert alert-info">
                      <Translator
                        file="module-resourceManager"
                        name="resource.setting.no.periodicity.established.worldlex"
                      />
                    </div>
                  )}
                  {resourceObligation?.nextDateSettings
                    ?.firstPeriodStartDate && (
                    <div className="alert alert-warning">
                      <Translator
                        file="module-resourceManager"
                        name="resource.configuration.set.previously.start.modified"
                      />
                      <br />
                      <Translator
                        file="module-resourceManager"
                        name="resource.modify.settings.period.provided.managed"
                      />
                    </div>
                  )}
                  {values.firstPeriodStartDate && values.period && (
                    <>
                      {resourceObligation?.nextDateSettings
                        ?.firstPeriodStartDate ? (
                        <ul>
                          <li>
                            <Translator
                              file="module-resourceManager"
                              name="resource.current.period.remains.unchanged"
                              variables={{
                                starDate:
                                  resourceObligation?.previsions?.[1]
                                    ?.startDate,
                                endDate:
                                  resourceObligation?.previsions?.[1]?.endDate,
                              }}
                            />
                          </li>
                          <li>
                            <Translator
                              file="module-resourceManager"
                              name="resource.next.period.updated.new.frequency"
                              isHtml
                            />{' '}
                            <br />
                            <span>
                              {`${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-resourceManager"
                                  name="resource.from"
                                />
                              )} ${moment(
                                resourceObligation?.previsions?.[1]?.endDate,
                                'DD/MM/YYYY',
                              )
                                .add(1, 'days')
                                .format('DD/MM/YYYY')} ${ReactDOMServer.renderToString(
                                    <Translator
                                      file="module-resourceManager"
                                      name="resource.to"
                                    />
                                  )} ${moment(
                                resourceObligation?.previsions?.[1]?.endDate,
                                'DD/MM/YYYY',
                              )
                                .add(
                                  parseInt(values.period, 10),
                                  values.periodType,
                                )
                                .format('DD/MM/YYYY')} ${ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-resourceManager"
                                    name="resource.inclusive"
                                  />
                                )}.`}
                            </span>
                          </li>
                        </ul>
                      ) : (
                        <ul>
                          <li>
                            <Translator
                              file="module-resourceManager"
                              name="resource.first.period"
                            />
                            : <br />
                            <span>
                              {`${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-resourceManager"
                                  name="resource.from"
                                />
                              )} ${values.firstPeriodStartDate} ${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-resourceManager"
                                  name="resource.to"
                                />
                              )} ${moment(
                                values.firstPeriodStartDate,
                                'DD/MM/YYYY',
                              )
                                .add(
                                  parseInt(values.period, 10),
                                  values.periodType,
                                )
                                .subtract(1, 'days')
                                .format('DD/MM/YYYY')} ${ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-resourceManager"
                                    name="resource.inclusive"
                                  />
                                )}.`}
                            </span>
                          </li>
                          <li>
                            <Translator
                              file="module-resourceManager"
                              name="resource.next.period"
                            />
                            : <br />
                            <span>
                              {` ${ReactDOMServer.renderToString(
                                <Translator
                                  file="module-resourceManager"
                                  name="resource.from"
                                />
                              )} ${moment(
                                values.firstPeriodStartDate,
                                'DD/MM/YYYY',
                              )
                                .add(
                                  parseInt(values.period, 10),
                                  values.periodType,
                                )
                                .format('DD/MM/YYYY')} ${ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-resourceManager"
                                    name="resource.to"
                                  />
                                )} ${moment(
                                values.firstPeriodStartDate,
                                'DD/MM/YYYY',
                              )
                                .add(
                                  parseInt(values.period, 10) * 2,
                                  values.periodType,
                                )
                                .subtract(1, 'days')
                                .format('DD/MM/YYYY')} ${ReactDOMServer.renderToString(
                                  <Translator
                                    file="module-resourceManager"
                                    name="resource.inclusive"
                                  />
                                )}.`}
                            </span>
                          </li>
                        </ul>
                      )}
                    </>
                  )}
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-sm btn-primary float-right"
              >
                <Translator
                  file="module-resourceManager"
                  name="resource.configure"
                />
              </button>
            </form>
          )}
        </Formik>
      </AppModal>
    </>
  );
}
